import { useCallback, useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (
          siteKey: string,
          options: { action: string },
        ) => Promise<string>;
      };
    };
  }
}

const scriptPromises = new Map<string, Promise<void>>();

const RECAPTCHA_READY_TIMEOUT_MS = 10_000;
const RECAPTCHA_LOAD_ERROR =
  "reCAPTCHA failed to load. Please refresh and try again.";

function loadRecaptchaScript(siteKey: string): Promise<void> {
  const existing = scriptPromises.get(siteKey);
  if (existing) return existing;

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("reCAPTCHA script failed to load"));
    document.head.appendChild(script);
  });

  scriptPromises.set(siteKey, promise);

  promise.catch(() => {
    scriptPromises.delete(siteKey);
  });

  return promise;
}

export interface RecaptchaState {
  isRecaptchaReady: boolean;
  recaptchaError: string | null;
  resolveToken: () => Promise<string>;
}

export function useRecaptcha(siteKey: string, action: string): RecaptchaState {
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
  const actionRef = useRef(action);
  const siteKeyRef = useRef(siteKey);

  useEffect(() => {
    actionRef.current = action;
    siteKeyRef.current = siteKey;
  }, [siteKey, action]);

  useEffect(() => {
    setIsRecaptchaReady(false);
    setRecaptchaError(null);

    let cancelled = false;

    const timeoutId = setTimeout(() => {
      if (!cancelled) {
        setRecaptchaError(RECAPTCHA_LOAD_ERROR);
      }
    }, RECAPTCHA_READY_TIMEOUT_MS);

    loadRecaptchaScript(siteKey)
      .then(() => {
        if (!window.grecaptcha || !window.grecaptcha.enterprise) {
          if (!cancelled) {
            clearTimeout(timeoutId);
            setRecaptchaError(RECAPTCHA_LOAD_ERROR);
          }
          return;
        }
        window.grecaptcha.enterprise.ready(() => {
          if (!cancelled) {
            clearTimeout(timeoutId);
            setIsRecaptchaReady(true);
          }
        });
      })
      .catch(() => {
        if (!cancelled) {
          clearTimeout(timeoutId);
          setRecaptchaError(RECAPTCHA_LOAD_ERROR);
        }
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [siteKey]);

  const resolveToken = useCallback((): Promise<string> => {
    const enterprise = window.grecaptcha?.enterprise;
    if (!enterprise) {
      return Promise.reject(new Error(RECAPTCHA_LOAD_ERROR));
    }
    return enterprise.execute(siteKeyRef.current, {
      action: actionRef.current,
    });
  }, []);

  return { isRecaptchaReady, recaptchaError, resolveToken };
}
